import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/services/auth/authContext';
import Cookies from 'js-cookie';
import { Loader2 } from 'lucide-react';
import { decodeJWT, getUserSubscriptionStatus, isTokenExpired } from '@/utils/jwt';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [statusMessage, setStatusMessage] = useState('Completing sign in...');

  useEffect(() => {
    const handleCallback = async () => {
      // Get parameters from URL
      const token = searchParams.get('token');
      const status = searchParams.get('status');
      const error = searchParams.get('error');
      const trial = searchParams.get('trial');
      const restId = searchParams.get('rest_id');

      if (error || status === 'error') {
        // Handle error
        console.error('OAuth error:', error);
        setStatusMessage('Authentication failed');
        setTimeout(() => {
          navigate(`/restaurant-login?error=${encodeURIComponent(error || 'Authentication failed')}`);
        }, 1500);
        return;
      }

      if (!token) {
        // No token received
        setStatusMessage('No authentication token received');
        setTimeout(() => {
          navigate('/restaurant-login?error=no_token');
        }, 1500);
        return;
      }

      try {
        // Decode and validate JWT token
        setStatusMessage('Validating authentication...');
        const decoded = decodeJWT(token);
        
        if (!decoded) {
          console.error('Invalid JWT token');
          navigate('/restaurant-login?error=invalid_token');
          return;
        }

        if (isTokenExpired(token)) {
          console.error('JWT token expired');
          navigate('/restaurant-login?error=token_expired');
          return;
        }

        // Store the token with proper settings for cross-site requests
        Cookies.set('auth_token', token, {
          expires: 1, // 1 day
          sameSite: 'lax', // Allow cookie to be sent with navigation
          secure: window.location.protocol === 'https:', // Only secure in production
          path: '/' // Available across entire site
        });

        // Clear any demo mode flags since this is a real user login
        localStorage.removeItem('isDemo');
        localStorage.removeItem('demoPlan');
        localStorage.removeItem('demoAuthToken');

        // Check if this is a signup flow with extraction data
        const isSignupFlow = localStorage.getItem('oauth_signup_flow') === 'true';
        const hasPendingExtraction = localStorage.getItem('pending_extraction') === 'true';
        const extractionSessionToken = localStorage.getItem('extraction_session_token');
        const extractionItemsCount = localStorage.getItem('extraction_items_count');
        const wasCached = localStorage.getItem('extraction_was_cached') === 'true';
        
        // Restore extraction state to sessionStorage if present
        if (hasPendingExtraction && extractionSessionToken) {
          console.log('📦 Restoring extraction state from OAuth flow:', {
            isSignupFlow,
            hasPendingExtraction,
            itemsCount: extractionItemsCount
          });
          
          sessionStorage.setItem('preview_session_token', extractionSessionToken);
          sessionStorage.setItem('extraction_completed', 'true');
          if (extractionItemsCount) {
            sessionStorage.setItem('preview_items_extracted', extractionItemsCount);
          }
          if (wasCached) {
            sessionStorage.setItem('cached_extraction', 'true');
          }
        }
        
        // Clean up localStorage flags after restoring
        localStorage.removeItem('oauth_signup_flow');
        localStorage.removeItem('pending_extraction');
        localStorage.removeItem('extraction_session_token');
        localStorage.removeItem('extraction_items_count');
        localStorage.removeItem('extraction_was_cached');

        // ======================================================================
        // ACTIVATION FLOW DETECTION (Sales Portal Pre-Onboarding)
        // ======================================================================
        // Check if coming from activation link (sales-assisted flow)
        const activationToken = localStorage.getItem('activation_session_token');
        console.log('🔍 [OAuthCallback] Read from localStorage:', {
          activation_session_token: activationToken,
          type: typeof activationToken
        });

        if (activationToken && activationToken !== 'null') {
          console.log('🎬 [OAuthCallback] ACTIVATION FLOW: Customer claiming pre-onboarded account');
          console.log('🔑 [OAuthCallback] Activation token:', activationToken);

          // Store for plan selection to pass to Stripe checkout
          localStorage.setItem('plan_selection_session_token', activationToken);
          console.log('✅ [OAuthCallback] Stored plan_selection_session_token:', activationToken);

          setStatusMessage('Setting up your pre-onboarded restaurant...');

          // ALWAYS go to product selection first for activation flow
          // This allows customer to confirm/choose their product (Virtual Assistant vs Virtual Host)
          // The pre-selected product (if any) will be shown as default in ProductSelection
          const preSelectedProduct = localStorage.getItem('selected_product_type');
          console.log('🔍 [OAuthCallback] Pre-selected product:', preSelectedProduct);
          console.log('🛒 [OAuthCallback] Going to product selection for activation flow');

          navigate('/product-selection', {
            state: {
              fromOAuth: true,
              isActivation: true,
              onboarding: true,
              preSelectedProduct: preSelectedProduct // Pass to ProductSelection to pre-select
            }
          });
          return; // Exit early - no need to check subscription status
        } else if (activationToken === 'null') {
          console.error('❌ [OAuthCallback] activation_session_token is string "null"');
        } else {
          console.log('ℹ️ [OAuthCallback] No activation_session_token found (frictionless flow)');
        }
        // ======================================================================

        // Get user type and route accordingly
        const userType = decoded.user_type;
        setStatusMessage('Setting up your account...');
        
        try {
          // Refresh user data in auth context
          await refreshUser();
        } catch (refreshError) {
          console.warn('Error refreshing user data:', refreshError);
          // Continue with JWT data if refresh fails
        }

        setStatusMessage('Redirecting...');

        // Check if this is a post-activation re-auth for fresh JWT
        const postOAuthRedirect = localStorage.getItem('post_oauth_redirect');
        if (postOAuthRedirect) {
          console.log('✅ Post-activation re-auth detected, redirecting to:', postOAuthRedirect);
          localStorage.removeItem('post_oauth_redirect');
          localStorage.removeItem('sales_activation_complete');
          navigate(postOAuthRedirect);
          return;
        }

        if (userType === 'customer') {
          // Customer users - redirect to customer app/website
          // Customers don't have subscriptions or complex routing
          const customerRedirectUrl = process.env.REACT_APP_CUSTOMER_URL || 'https://browse.dineai.ai';
          window.location.href = `${customerRedirectUrl}?token=${token}&status=success`;
          return;
        }

        // Restaurant users - handle subscription-based routing
        const subscriptionStatus = getUserSubscriptionStatus(token);
        
        if (!subscriptionStatus) {
          navigate('/restaurant-login?error=user_data_error');
          return;
        }

        // Extract user name from JWT for personalized welcome (first name only)
        const firstName = decoded.first_name || '';
        const displayName = firstName || decoded.email?.split('@')[0] || 'there';

        // ======================================================================
        // BACKEND IS SOURCE OF TRUTH - Trust JWT over localStorage flags
        // ======================================================================
        // If backend says user has restaurant and doesn't need onboarding,
        // they're an EXISTING user logging back in. Don't check localStorage flags.
        const isExistingUserWithRestaurant = !subscriptionStatus.needsOnboarding && subscriptionStatus.hasRestaurant && restId;

        console.log('🔍 OAuth routing decision - Backend source of truth:', {
          needsOnboarding: subscriptionStatus.needsOnboarding,
          hasRestaurant: subscriptionStatus.hasRestaurant,
          restId,
          isExistingUserWithRestaurant,
          hasActiveSubscription: subscriptionStatus.hasActiveSubscription,
          isTrialActive: subscriptionStatus.isTrialActive,
          subscriptionRequired: subscriptionStatus.subscriptionRequired
        });

        // EXISTING USER: If backend says they have a restaurant, go to dashboard
        // Don't let stale localStorage/sessionStorage flags override this decision
        if (isExistingUserWithRestaurant && (subscriptionStatus.hasActiveSubscription || subscriptionStatus.isTrialActive || !subscriptionStatus.subscriptionRequired)) {
          console.log('✅ EXISTING USER with restaurant - Backend says go to dashboard, ignoring localStorage flags');
          // Clear ALL extraction flags to prevent future confusion
          sessionStorage.removeItem('extraction_completed');
          sessionStorage.removeItem('preview_session_token');
          sessionStorage.removeItem('preview_items_extracted');
          sessionStorage.removeItem('cached_extraction');
          navigate('/restaurant-dashboard');
          return;
        }

        // Check if user has extraction data from before signup
        const hasExtractedMenu = sessionStorage.getItem('extraction_completed') === 'true';
        const itemsExtracted = sessionStorage.getItem('preview_items_extracted');

        // Check if this is a new signup (either via trial param or needs_onboarding flag)
        const isNewSignup = trial === 'true' || subscriptionStatus.needsOnboarding || !subscriptionStatus.hasRestaurant;

        console.log('🔍 OAuth routing decision - Signup flow check:', {
          trial,
          isNewSignup,
          hasExtractedMenu,
          itemsExtracted
        });

        if (isNewSignup) {
          // Get restaurant prefill data from sessionStorage (from search flow)
          const prefillDataStr = sessionStorage.getItem('restaurant_prefill_data');
          const prefillData = prefillDataStr ? JSON.parse(prefillDataStr) : null;

          // New user - go to product selection first to choose Virtual Assistant or Virtual Host
          // Product selection will route to plan selection, which handles restaurant-setup
          if (hasExtractedMenu) {
            // New signup with extracted menu - go to product selection, preserve extraction data
            console.log('🚀 New signup with extraction, going to product selection');
            navigate('/product-selection', {
              state: {
                hasExtractedMenu: true,
                itemsExtracted: itemsExtracted,
                fromOAuth: true,
                prefillData: prefillData,
                onboarding: true
              }
            });
          } else {
            // Regular new user - go to product selection
            console.log('🚀 New signup without extraction, going to product selection');
            navigate('/product-selection', {
              state: {
                fromOAuth: true,
                prefillData: prefillData,
                onboarding: true
              }
            });
          }
        } else if (subscriptionStatus.hasActiveSubscription || subscriptionStatus.isTrialActive) {
          // User has restaurant and active subscription
          // Check if they have a menu uploaded
          try {
            const menuResponse = await fetch('/api/restaurants/active-menu', {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (menuResponse.ok) {
              const menu = await menuResponse.json();
              if (menu && menu.has_menu) {
                // Has menu - go to dashboard
                navigate('/restaurant-dashboard');
              } else {
                // No menu yet - go to menu upload
                navigate('/restaurant-menu-upload');
              }
            } else {
              // Can't check menu, default to menu upload for new users
              navigate('/restaurant-menu-upload');
            }
          } catch (error) {
            console.error('Error checking menu status:', error);
            navigate('/restaurant-menu-upload');
          }
        } else if (subscriptionStatus.subscriptionRequired) {
          // User has restaurant but needs subscription
          // Check if they have a product selected, if not go to product selection first
          const preSelectedProduct = localStorage.getItem('selected_product_type');
          const prefillDataStr = sessionStorage.getItem('restaurant_prefill_data');
          const prefillData = prefillDataStr ? JSON.parse(prefillDataStr) : null;

          if (preSelectedProduct) {
            // Product already selected, go to plan selection
            if (hasExtractedMenu) {
              navigate(`/plan-selection?required=true&name=${encodeURIComponent(displayName)}&frictionless=true&items=${itemsExtracted || 0}`);
            } else {
              navigate(`/plan-selection?required=true&name=${encodeURIComponent(displayName)}`);
            }
          } else {
            // No product selected - go to product selection first
            console.log('🛒 User needs subscription but no product selected, going to product selection');
            navigate('/product-selection', {
              state: {
                hasExtractedMenu,
                itemsExtracted,
                fromOAuth: true,
                prefillData,
                onboarding: true,
                subscriptionRequired: true
              }
            });
          }
        } else {
          // Fallback - check for menu
          navigate('/restaurant-menu-upload');
        }

      } catch (error) {
        console.error('Error handling OAuth callback:', error);
        setStatusMessage('Authentication processing failed');
        setTimeout(() => {
          navigate('/restaurant-login?error=callback_failed');
        }, 1500);
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-dineai-blue flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">{statusMessage}</p>
      </div>
    </div>
  );
};

export default OAuthCallback;